<template>
  <v-container fluid class="container">
    <Breadcrumbs
      :items="breadcrumbs"/>
    <v-toolbar flat>
      <ejs-dropdownlist id='dropdownlist' ref='dropdown' :dataSource='dropdownDataSource' :change='onTypeDropdownChange'
                        v-model="typeDropdown" placeholder='Report Type' width='200'></ejs-dropdownlist>
      <ejs-dropdownlist id='dropdownlist_currency' ref='dropdown_currency' :dataSource='currencyDropdownDataSource' :change='onCurrencyDropdownChange'
                        v-model="currencyDropdown" placeholder='Currency' width='200' ></ejs-dropdownlist>
      <ejs-dropdownlist id='dropdownlist_data_filter' ref='dropdown_data_filter' :dataSource='dataFilterDropdownDataSource' :change='onDataFilterDropdownChange'
                        v-model="dataFilterDropdown" placeholder='Data Filter' width='160' class="ml-2"></ejs-dropdownlist>
      <v-spacer></v-spacer>
      <v-btn-toggle v-model="calcMode" mandatory class="mr-4" @change="onCalcModeChange">
        <v-btn value="direct" small>Direct</v-btn>
        <v-btn value="solver" small>Solver</v-btn>
      </v-btn-toggle>
      <v-btn color="primary" @click='excelDownloadClick'>
        Export to Excel
      </v-btn>
    </v-toolbar>
    <v-spacer></v-spacer>
    <ejs-treegrid :dataSource='items' v-if='items' childMapping='sub' :treeColumnIndex='0' :height='560' :allowExcelExport='true'
                  :allowTextWrap="true" :excelHeaderQueryCellInfo='excelHeader'>
        <e-columns>
            <e-column field='summary_pos' headerText='Summary - Position Mapping' width='230'></e-column>
            <e-column field='position' headerText='Position' width='170'></e-column>
            <e-column textAlign='center' :columns='valuesColumns'>
            </e-column>
        </e-columns>
    </ejs-treegrid>
    <Snackbar :text="notificationText"
      :type="notificationType" />
    <v-overlay :value="loader"
      :z-index="1000">
      <v-progress-circular indeterminate
        size="64"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>
<script>
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import {
  TreeGridPlugin, ExcelExport,
} from '@syncfusion/ej2-vue-treegrid';
import Breadcrumbs from '@/components/Breadcrumbs';
import Snackbar from '@/components/Snackbar';
import api from '../api/lbt-api';

Vue.use(TreeGridPlugin);

const {
  getSummaryPortfolioForPlanning,
  getContractCalculationMode,
  setContractCalculationMode,
} = api;

export default {
  name: 'SummaryPortfolio',
  components: {
    Breadcrumbs,
    Snackbar,
  },
  data() {
    return {
      notificationText: '',
      loader: false,
      notificationType: null,
      headers: [],
      items: [],
      expanded: [],
      breadcrumbs: [
        {
          translationKey: 'breadcrumbs_plannings',
          route: '/plannings',
          disabled: false,
        },
        {
          translationKey: 'portfolio',
          disabled: true,
        },
      ],
      valuesColumns: [],
      highlightCellAttributes: { class: 'highlight-cell' },
      typeDropdown: 'Product Type',
      typeDropdownValue: 'product_type',
      dropdownDataSource: ['Product Type', 'Segment', 'Leasing Type'],
      currencyDropdown: 'LCY',
      calcMode: 'direct',
      currencyDropdownValue: 'lcy',
      currencyDropdownDataSource: ['LCY', 'EUR'],
      dataFilterDropdown: 'All',
      dataFilterDropdownValue: 'all',
      dataFilterDropdownDataSource: ['All', 'New', 'Existing'],
    };
  },
  computed: {
    ...mapGetters('translations', [
      'translate',
    ]),
    ...mapState('plannings', [
      'activePlanning',
    ]),
  },
  async created() {
    try {
      const setting = await getContractCalculationMode();
      this.calcMode = setting.value || 'direct';
    } catch (e) {
      this.calcMode = 'direct';
    }
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loader = true;
      try {
        const planning = await getSummaryPortfolioForPlanning(this.$route.params.planningId, this.typeDropdownValue, this.currencyDropdownValue, this.dataFilterDropdownValue);
        this.headers = planning.periods;
        this.items = planning.rows;
        this.prepareValuesGridHeaders();
      } catch (message) {
        this.notificationText = message;
        this.notificationType = 'error';
        setTimeout(() => { this.notificationText = null; }, 100);
      } finally {
        this.loader = false;
      }
    },
    toggleExpandedItem(item) {
      // eslint-disable-next-line
      item.isExpanded = !item.isExpanded;
      this.expanded.push(item);
    },
    prepareValuesGridHeaders() {
      this.valuesColumns = [];
      const item = this.items[0];
      for (let i = 0; i < item.values.length; i += 1) {
        this.valuesColumns.push({
          field: `values.${i}`,
          headerText: i,
          headerValueAccessor: this.lookupValuesHeader,
          customAttributes: this.highlightCellAttributes,
          width: 55,
          format: 'n2',
          textAlign: 'right',
        });
      }
    },
    lookupValuesHeader(idx) {
      return this.headers[Number(idx)];
    },
    async onCalcModeChange(mode) {
      try {
        await setContractCalculationMode(mode);
      } catch (e) {
        this.notificationText = e.message;
        this.notificationType = 'error';
        setTimeout(() => { this.notificationText = null; }, 3000);
      }
      await this.fetchData();
    },
    onTypeDropdownChange(selected) {
      const { value } = selected;
      switch (value) {
        case 'Product Type':
          this.typeDropdownValue = 'product_type';
          break;
        case 'Segment':
          this.typeDropdownValue = 'segment';
          break;
        case 'Leasing Type':
          this.typeDropdownValue = 'leasing_type';
          break;
        default:
          break;
      }

      setTimeout(this.fetchData, 100);
    },
    onCurrencyDropdownChange(selected) {
      const { value } = selected;
      switch (value) {
        case 'LCY':
          this.currencyDropdownValue = 'lcy';
          break;
        case 'EUR':
          this.currencyDropdownValue = 'eur';
          break;
        default:
          break;
      }

      setTimeout(this.fetchData, 100);
    },
    onDataFilterDropdownChange(selected) {
      const { value } = selected;
      switch (value) {
        case 'New':
          this.dataFilterDropdownValue = 'new';
          break;
        case 'Existing':
          this.dataFilterDropdownValue = 'existing';
          break;
        default:
          this.dataFilterDropdownValue = 'all';
          break;
      }

      setTimeout(this.fetchData, 100);
    },
    excelDownloadClick() {
      const grid = document.getElementsByClassName('e-grid')[0].ej2_instances[0];
      const excelExportProperties = {
        exportType: 'CurrentPage',
        fileName: `Summary portfolio - Planning - ${this.activePlanning.year}-${this.activePlanning.month}.xlsx`,
      };
      grid.excelExport(excelExportProperties);
    },
    excelHeader(args) {
      const { cell } = args;
      if (cell.index > 1) {
        cell.value = this.headers[cell.value];
      }
    },
  },
  provide: {
    treegrid: [ExcelExport],
  },
};
</script>

<style>
  @import '../../node_modules/@syncfusion/ej2-vue-treegrid/styles/material.css';

  .e-grid .e-headercell.highlight-cell, .highlight-cell {
    background: #eeeeee;
  }
  .e-valid-input {
    margin-right: 10px;
  }
  .e-headercelldiv {
    text-align: center !important;
  }
</style>
